import { useState, useEffect, useRef, useCallback } from "react";
import { PrinterIcon } from "@heroicons/react/24/outline";
import PageHeader from "../../components/layout/PageHeader";
import toast from "react-hot-toast";
import { reportsApi } from "../../api/reports";
import { useSeason } from "../../context/SeasonContext";
import { formatCabinNumber, normalizeDivisionName, parseCabinNumber } from "../../utils/terminology";
import type { StampingReportDivision, StampingReportCabin, StampingReportCamper } from "../../types";

const PRINT_PAGE_HEIGHT_PX = 960;
const PRINT_PAGE_WIDTH_PX = 720;
const FONT_SIZE_MAX_PT = 14;
const FONT_SIZE_MIN_PT = 9;

const PRINT_STYLE = `
@media print {
  @page { size: letter portrait; margin: 0.5in; }
  .no-print { display: none !important; }
  .division-section { break-after: page; page-break-after: always; }
  .division-section:last-child { break-after: auto; page-break-after: auto; }
  .cabin-section { break-inside: avoid; page-break-inside: avoid; }
  body { background: white !important; }
  .print-container { padding: 0 !important; }
}
`;

// ─── Sub-components ───────────────────────────────────────────────────────────

function stampGroups(camper: StampingReportCamper): { label: string; text: string }[] {
  const acs = camper.activities.filter((a) => a.yearAcCount > 0);
  const honors = camper.activities.filter((a) => a.honorCount > 0);
  const grads = camper.activities.filter((a) => a.hasGraduation);

  const groups: { label: string; text: string }[] = [];
  if (acs.length > 0) {
    groups.push({
      label: "ACs",
      text: acs.map((a) => `${a.activityName}: ${a.yearAcCount}`).join(" ● "),
    });
  }
  if (honors.length > 0) {
    groups.push({
      label: "Honors",
      text: honors.map((a) => `${a.activityName}: ${a.honorCount}`).join(" ● "),
    });
  }
  if (grads.length > 0) {
    groups.push({
      label: "Grad ★",
      text: grads.map((a) => a.activityName).join(" ● "),
    });
  }
  return groups;
}

function CamperRow({ camper, isFirstTerm }: { camper: StampingReportCamper; isFirstTerm: boolean }) {
  // Full-term campers keep their kerchiefs through both sessions, so their
  // stamps are held until the end of 2nd Term. On the 1st Term report we
  // list them by name only, with a note instead of their stamp counts.
  const holdForSecondTerm = isFirstTerm && camper.isFullTerm;
  const groups = stampGroups(camper);

  return (
    <tr className="border-t border-stone-200">
      <td className="py-1 pr-3 font-medium whitespace-nowrap">
        {camper.lastName}, {camper.firstName}
        {camper.isFullTerm && (
          <span className="ml-1.5 text-xs font-normal text-stone-500 italic">Full Term</span>
        )}
      </td>
      <td className="py-1 pl-2">
        {holdForSecondTerm ? (
          <span className="text-stone-500 italic">
            Full Term camper — do not stamp until 2nd Term
          </span>
        ) : groups.length === 0 ? (
          "—"
        ) : (
          groups.map((group, i) => (
            <span key={group.label}>
              {i > 0 && <span className="text-stone-400"> | </span>}
              <span className="font-bold">{group.label}:</span> {group.text}
            </span>
          ))
        )}
      </td>
    </tr>
  );
}

function CabinSection({ cabin, isFirstTerm }: { cabin: StampingReportCabin; isFirstTerm: boolean }) {
  if (cabin.campers.length === 0 && cabin.counselors.length === 0) return null;

  return (
    <div className="cabin-section border border-stone-500 rounded mb-3 p-2">
      <div className="font-bold mb-1">{formatCabinNumber(cabin.name)}</div>
      {cabin.campers.length > 0 && (
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-stone-400">
              <th className="pb-1 pr-3 text-left font-semibold whitespace-nowrap">Camper</th>
              <th className="pb-1 pl-2 text-left font-semibold">Stamps Needed This Session</th>
            </tr>
          </thead>
          <tbody>
            {cabin.campers.map((camper) => (
              <CamperRow key={camper.id} camper={camper} isFirstTerm={isFirstTerm} />
            ))}
          </tbody>
        </table>
      )}
      {cabin.counselors.length > 0 && (
        <div className="mt-2 text-stone-600 italic">
          Counselors: {cabin.counselors.join(", ")}
        </div>
      )}
    </div>
  );
}

function DivisionSection({
  division,
  isFirstTerm,
  fontSize,
  divRef,
}: {
  division: StampingReportDivision;
  isFirstTerm: boolean;
  fontSize: number;
  divRef: (el: HTMLElement | null) => void;
}) {
  return (
    <section
      className="division-section pt-4"
      ref={divRef}
      style={{ fontSize: `${fontSize}pt` }}
    >
      <h2 className="font-bold text-center mb-3" style={{ fontSize: `${fontSize + 3}pt` }}>
        {normalizeDivisionName(division.name)}
      </h2>
      {division.cabins
        .filter((c) => parseCabinNumber(c.name) != null)
        .sort((a, b) => (parseCabinNumber(b.name) ?? 0) - (parseCabinNumber(a.name) ?? 0))
        .map((cabin) => (
          <CabinSection key={cabin.id} cabin={cabin} isFirstTerm={isFirstTerm} />
        ))}
    </section>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function KerchiefStampingReport() {
  const { selectedTermId, selectedYear, selectedTerm } = useSeason();
  const [divisions, setDivisions] = useState<StampingReportDivision[]>([]);
  const [isFirstTerm, setIsFirstTerm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fontSizes, setFontSizes] = useState<Record<number, number>>({});

  const divRefs = useRef<Record<number, HTMLElement | null>>({});

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = PRINT_STYLE;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  useEffect(() => {
    if (selectedTermId === null) {
      setDivisions([]);
      setIsFirstTerm(false);
      return;
    }
    setLoading(true);
    setFontSizes({});
    reportsApi
      .kerchiefStamping(selectedTermId)
      .then((response) => {
        setDivisions(response.divisions);
        setIsFirstTerm(response.isFirstTerm);
      })
      .catch(() => toast.error("Failed to load kerchief stamping report"))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTermId, selectedYear]);

  useEffect(() => {
    if (loading || divisions.length === 0) return;

    const sizes: Record<number, number> = {};

    for (const division of divisions) {
      const el = divRefs.current[division.id];
      if (!el) continue;

      let fontSize = FONT_SIZE_MAX_PT;
      el.style.fontSize = `${fontSize}pt`;
      el.style.maxWidth = `${PRINT_PAGE_WIDTH_PX}px`;

      while (
        fontSize > FONT_SIZE_MIN_PT &&
        (el.scrollHeight > PRINT_PAGE_HEIGHT_PX || el.scrollWidth > PRINT_PAGE_WIDTH_PX)
      ) {
        fontSize--;
        el.style.fontSize = `${fontSize}pt`;
      }

      el.style.maxWidth = "";
      sizes[division.id] = fontSize;
    }

    // Use the smallest computed size for all divisions so text is uniform throughout
    const minSize = Math.min(...Object.values(sizes));
    const uniformSizes: Record<number, number> = {};
    for (const division of divisions) uniformSizes[division.id] = minSize;
    setFontSizes(uniformSizes);
  }, [loading, divisions]);

  const setDivRef = useCallback((id: number) => (el: HTMLElement | null) => {
    divRefs.current[id] = el;
  }, []);

  const sessionLabel = isFirstTerm ? "1st Term session" : "2nd Term / Full Term session";

  return (
    <div>
      <PageHeader
        title={`Kerchief Stamping Report${selectedTerm ? ` — ${selectedTerm.year} ${selectedTerm.name}` : ""}`}
      />

      <div className="no-print flex flex-wrap items-center gap-4 mb-6 px-6">
        <button
          onClick={() => window.print()}
          disabled={selectedTermId === null || divisions.length === 0}
          className="flex items-center gap-1.5 rounded-md bg-forest-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-forest-700 focus:outline-none focus:ring-2 focus:ring-forest-500 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <PrinterIcon className="w-4 h-4" />
          Print / Save as PDF
        </button>
        {selectedTermId !== null && !loading && (
          <span className="text-sm text-stone-500 italic">{sessionLabel}</span>
        )}
      </div>

      <div className="print-container px-6">
        {selectedTermId === null ? (
          <div className="no-print rounded-md border border-amber-300 bg-amber-50 px-5 py-4 text-amber-800 text-sm">
            Select a specific term (1st Term or 2nd Term) to generate the Kerchief Stamping Report.
            &ldquo;All Campers&rdquo; is not supported for this report.
          </div>
        ) : loading ? (
          <div className="no-print flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-forest-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : divisions.length === 0 ? (
          <div className="no-print text-center py-16 text-stone-500">
            No data found for this term. Make sure campers are enrolled and assigned to cabins in a division.
          </div>
        ) : (
          divisions
            .map((division) => ({
              division,
              maxCabin: Math.max(
                0,
                ...division.cabins.map((c) => parseCabinNumber(c.name) ?? 0),
              ),
            }))
            .filter((d) => d.maxCabin > 0)
            .sort((a, b) => b.maxCabin - a.maxCabin)
            .map(({ division }) => (
              <DivisionSection
                key={division.id}
                division={division}
                isFirstTerm={isFirstTerm}
                fontSize={fontSizes[division.id] ?? FONT_SIZE_MAX_PT}
                divRef={setDivRef(division.id)}
              />
            ))
        )}
      </div>
    </div>
  );
}
